import { useEffect, useRef, useCallback, useState } from 'react';

export function useWebSocket(onMessage) {
    const wsRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const reconnectTimeoutRef = useRef(null);
    const onMessageRef = useRef(onMessage);
    const subscriptionsRef = useRef(new Set());

    // onMessage 최신 참조 유지 (소켓 재생성 없이 최신 핸들러 사용)
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    const connect = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) return;

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const ws = new WebSocket(`${protocol}//${window.location.host}`);

        ws.onopen = () => {
            console.log('WebSocket connected');
            setIsConnected(true);
            // 최초 연결 레이스 + 재연결 시 구독 복구
            for (const buildId of subscriptionsRef.current) {
                ws.send(JSON.stringify({ type: 'subscribe', buildId }));
            }
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                onMessageRef.current?.(data);
            } catch (err) {
                console.error('WebSocket message parse error:', err);
            }
        };

        ws.onclose = (event) => {
            console.log('WebSocket disconnected', event);
            setIsConnected(false);
            // Reconnect after 3 seconds
            reconnectTimeoutRef.current = setTimeout(connect, 3000);
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        wsRef.current = ws;
    }, []);

    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    }, []);

    const subscribe = useCallback((buildId) => {
        subscriptionsRef.current.add(buildId);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'subscribe', buildId }));
        }
    }, []);

    const unsubscribe = useCallback((buildId) => {
        subscriptionsRef.current.delete(buildId);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'unsubscribe', buildId }));
        }
    }, []);

    useEffect(() => {
        connect();
        return () => disconnect();
    }, [connect, disconnect]);

    return {
        isConnected,
        subscribe,
        unsubscribe,
    };
}
